/**
 * Limited collection (cok-satanlar): lock the initial 25 products, then
 * filter + sort client-side so Shopify never re-queries the full collection.
 */
(function () {
  const HIDDEN_CLASS = 'is-byte-filter-hidden';
  const ROOT_SELECTOR = '[data-byte-limited-collection]';
  const ORIGINAL_SORT_KEYS = new Set(['', 'manual', 'best-selling', 'most-relevant']);

  class ByteLimitedCollectionFilters {
    constructor(root) {
      this.root = root;
      this.products = this.#parseJson('[data-byte-limited-collection-products]') || [];
      this.filtersMeta = this.#parseJson('[data-byte-limited-collection-filters]') || [];
      this.boundHandleFormInput = this.handleFormInput.bind(this);
      this.boundHandleFacetRemove = this.handleFacetRemove.bind(this);
      this.originalOrder = this.products.map((product) => String(product.id));
    }

    init() {
      if (!this.products.length) return;

      if (this.#shouldResetToLockedSet()) {
        const url = new URL(window.location.href);
        const pending = {
          sortBy: url.searchParams.get('sort_by') || '',
          filters: {}
        };

        for (const key of [...url.searchParams.keys()]) {
          if (!key.startsWith('filter.')) continue;
          pending.filters[key] = url.searchParams.getAll(key);
          url.searchParams.delete(key);
        }

        url.searchParams.delete('sort_by');
        url.searchParams.delete('page');
        sessionStorage.setItem('byte-limited-collection-pending', JSON.stringify(pending));
        window.location.replace(url.pathname + url.search);
        return;
      }

      this.#patchFacetFiltersForm();
      this.#bindEvents();
      this.#restorePendingState();
      this.applyFromDom();
    }

    applyFromDom() {
      const selectedFilters = this.#getSelectedFiltersFromDom();
      const sortBy = this.#getSelectedSortFromDom();
      this.apply(selectedFilters, sortBy);
    }

    handleFormInput(event) {
      const form = event.target.closest('form');
      if (!form || !this.root.contains(form)) return;

      const name = event.target.name || '';
      if (name !== 'sort_by' && !name.startsWith('filter.')) return;

      event.stopImmediatePropagation();
      this.applyFromDom();
    }

    handleFacetRemove(event) {
      const removeLink = event.target.closest('facet-remove a');
      if (!removeLink || !this.root.contains(removeLink)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        removeLink.classList.contains('active-facets__button-remove') ||
        removeLink.hasAttribute('data-byte-clear-all')
      ) {
        this.#clearAllFilterInputs();
        this.applyFromDom();
        return;
      }

      const paramName = removeLink.getAttribute('data-byte-filter-name');
      const value = removeLink.getAttribute('data-byte-filter-value');

      if (paramName != null && value != null) {
        const inputs = this.root.querySelectorAll(
          `input[type="checkbox"][name="${CSS.escape(paramName)}"][value="${CSS.escape(value)}"]`
        );
        for (const input of inputs) {
          input.checked = false;
        }
        this.applyFromDom();
        return;
      }

      this.#clearAllFilterInputs();
      this.applyFromDom();
    }

    apply(selectedFilters, sortBy) {
      const matchingProducts = this.products.filter((product) =>
        this.#productMatchesFilters(product, selectedFilters)
      );
      const sortedProducts = this.#sortProducts(matchingProducts, sortBy);
      const matchingIds = new Set(sortedProducts.map((product) => String(product.id)));

      this.#reorderGrid(sortedProducts);
      this.#toggleVisibility(matchingIds);
      this.#updateProductCount(matchingIds.size);
      this.#updateFacetCounts(selectedFilters);
      this.#syncActiveFacetPills(selectedFilters);
      this.#clearLoadingState();
      this.root.setAttribute('data-byte-filters-ready', '');
    }

    #shouldResetToLockedSet() {
      const url = new URL(window.location.href);
      const hasFilters = [...url.searchParams.keys()].some((key) => key.startsWith('filter.'));
      const sortBy = url.searchParams.get('sort_by') || '';
      const hasNonOriginalSort = sortBy !== '' && !ORIGINAL_SORT_KEYS.has(sortBy);
      return hasFilters || hasNonOriginalSort;
    }

    #restorePendingState() {
      const raw = sessionStorage.getItem('byte-limited-collection-pending');
      if (!raw) return;

      sessionStorage.removeItem('byte-limited-collection-pending');

      try {
        const pending = JSON.parse(raw);
        if (pending.sortBy) {
          const sortSelects = this.root.querySelectorAll('select[name="sort_by"]');
          for (const select of sortSelects) {
            select.value = pending.sortBy;
          }
        }

        if (pending.filters) {
          for (const [name, values] of Object.entries(pending.filters)) {
            for (const value of values) {
              const input = this.root.querySelector(
                `input[type="checkbox"][name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`
              );
              if (input) input.checked = true;
            }
          }
        }
      } catch (error) {
        console.error('Byte limited collection pending state restore failed', error);
      }
    }

    #patchFacetFiltersForm() {
      if (typeof FacetFiltersForm === 'undefined') return;
      if (FacetFiltersForm.__byteLimitedPatched) return;

      const originalRenderPage = FacetFiltersForm.renderPage.bind(FacetFiltersForm);

      FacetFiltersForm.renderPage = (searchParams, event, updateURLHash = true) => {
        if (!document.querySelector(ROOT_SELECTOR)) {
          originalRenderPage(searchParams, event, updateURLHash);
          return;
        }

        const instance = document.querySelector(ROOT_SELECTOR)?.__byteLimitedFilters;
        if (instance) {
          instance.applyFromDom();
          return;
        }

        originalRenderPage(searchParams, event, updateURLHash);
      };

      FacetFiltersForm.__byteLimitedPatched = true;
    }

    #bindEvents() {
      this.root.addEventListener('input', this.boundHandleFormInput, true);
      this.root.addEventListener('change', this.boundHandleFormInput, true);
      this.root.addEventListener('click', this.boundHandleFacetRemove, true);
      this.root.__byteLimitedFilters = this;
    }

    #getSelectedFiltersFromDom() {
      const selected = {};
      const inputs = this.root.querySelectorAll(
        '#FacetFiltersForm input[type="checkbox"][name^="filter."]:checked, #FacetFiltersFormMobile input[type="checkbox"][name^="filter."]:checked'
      );

      for (const input of inputs) {
        if (!selected[input.name]) selected[input.name] = [];
        selected[input.name].push(input.value);
      }

      return selected;
    }

    #getSelectedSortFromDom() {
      const select =
        this.root.querySelector('#FacetSortForm select[name="sort_by"]') ||
        this.root.querySelector('select[name="sort_by"]');
      return select?.value || 'best-selling';
    }

    #productMatchesFilters(product, selectedFilters) {
      for (const [paramName, values] of Object.entries(selectedFilters)) {
        if (!values.length) continue;
        const matched = values.some((value) => this.#productHasFilterValue(product, paramName, value));
        if (!matched) return false;
      }
      return true;
    }

    #productHasFilterValue(product, paramName, value) {
      const directValues = product.filterValues?.[paramName];

      if (Array.isArray(directValues)) {
        return directValues.some((matchValue) => this.#valuesMatch(matchValue, value));
      }

      const filterMeta = this.filtersMeta.find((filter) => filter.paramName === paramName);

      if (paramName.includes('filter.p.tag')) {
        return (product.tags || []).some((tag) => this.#valuesMatch(tag, value));
      }

      if (paramName.includes('filter.p.vendor')) {
        return this.#valuesMatch(product.vendor, value);
      }

      if (paramName.includes('filter.p.product_type')) {
        return this.#valuesMatch(product.productType, value);
      }

      if (paramName.includes('filter.v.availability')) {
        const normalizedValue = this.#normalize(value);
        const isAvailable = product.available === true;
        return (normalizedValue === '1' && isAvailable) || (normalizedValue === '0' && !isAvailable);
      }

      if (paramName.includes('filter.v.option') && filterMeta?.label && product.options?.[filterMeta.label]) {
        return product.options[filterMeta.label].some((optionValue) => this.#valuesMatch(optionValue, value));
      }

      return (product.matchValues || []).some((matchValue) => this.#valuesMatch(matchValue, value));
    }

    #sortProducts(products, sortBy) {
      const list = [...products];

      switch (sortBy) {
        case 'price-ascending':
          return list.sort((a, b) => Number(a.price) - Number(b.price));
        case 'price-descending':
          return list.sort((a, b) => Number(b.price) - Number(a.price));
        case 'title-ascending':
          return list.sort((a, b) => String(a.title).localeCompare(String(b.title), 'tr'));
        case 'title-descending':
          return list.sort((a, b) => String(b.title).localeCompare(String(a.title), 'tr'));
        case 'created-ascending':
          return list.sort((a, b) => Number(a.createdAt) - Number(b.createdAt));
        case 'created-descending':
          return list.sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
        case 'best-selling':
        case 'manual':
        case 'most-relevant':
        default:
          return list.sort(
            (a, b) => this.originalOrder.indexOf(String(a.id)) - this.originalOrder.indexOf(String(b.id))
          );
      }
    }

    #reorderGrid(sortedProducts) {
      const grid = this.root.querySelector('#product-grid');
      if (!grid) return;

      for (const product of sortedProducts) {
        const item = grid.querySelector(`[data-byte-product-id="${product.id}"]`);
        if (item) grid.appendChild(item);
      }
    }

    #toggleVisibility(matchingIds) {
      const gridItems = this.root.querySelectorAll('#product-grid [data-byte-product-id]');
      for (const item of gridItems) {
        const productId = item.getAttribute('data-byte-product-id');
        item.classList.toggle(HIDDEN_CLASS, !matchingIds.has(productId));
      }
    }

    #updateFacetCounts(selectedFilters) {
      const inputs = this.root.querySelectorAll(
        '#FacetFiltersForm input[type="checkbox"][name^="filter."], #FacetFiltersFormMobile input[type="checkbox"][name^="filter."]'
      );

      for (const input of inputs) {
        const paramName = input.name;
        const value = input.value;
        const filterMeta = this.filtersMeta.find((filter) => filter.paramName === paramName);
        const isOrOperator = (filterMeta?.operator || 'OR').toUpperCase() !== 'AND';

        // Count products that have THIS value and still match other active facets.
        // For OR facets, ignore sibling selections in the same group so Hardal
        // does not inflate Gri's count.
        const hypotheticalFilters = {};

        for (const [key, values] of Object.entries(selectedFilters)) {
          if (key === paramName && isOrOperator) continue;
          hypotheticalFilters[key] = [...values];
        }

        if (isOrOperator) {
          hypotheticalFilters[paramName] = [value];
        } else if (input.checked) {
          hypotheticalFilters[paramName] = [...(selectedFilters[paramName] || [])];
        } else {
          hypotheticalFilters[paramName] = [...(selectedFilters[paramName] || []), value];
        }

        const count = this.products.filter((product) =>
          this.#productMatchesFilters(product, hypotheticalFilters)
        ).length;

        this.#setInputCount(input, count);
      }
    }

    #setInputCount(input, count) {
      const label = input.closest('label, .facets__label, .facet-checkbox');
      if (!label) return;

      const textLabel = label.querySelector('.facet-checkbox__text-label');
      const visibleCount = label.querySelector('.facet-checkbox__text');
      const visuallyHidden = label.querySelector('.visually-hidden');

      if (visibleCount && textLabel) {
        const labelText = textLabel.textContent.trim();
        visibleCount.innerHTML = `<span class="facet-checkbox__text-label">${this.#escapeHtml(
          labelText
        )}</span> (${count})`;
      }

      if (visuallyHidden && textLabel) {
        visuallyHidden.textContent = `${textLabel.textContent.trim()} (${count})`;
      }

      const isDisabled = count === 0 && !input.checked;
      input.disabled = isDisabled;
      label.classList.toggle('disabled', isDisabled);

      const listItem = input.closest('.facets__item, .mobile-facets__item');
      if (listItem) {
        listItem.classList.toggle('is-byte-filter-option-hidden', isDisabled);
        listItem.hidden = isDisabled;
      }
    }

    #updateProductCount(count) {
      const countText = this.#formatProductCount(count);
      const containers = [
        document.getElementById('ProductCount'),
        document.getElementById('ProductCountDesktop'),
        ...this.root.querySelectorAll('.mobile-facets__count')
      ];

      for (const container of containers) {
        if (!container) continue;
        container.textContent = countText;
        container.classList.remove('loading');
      }
    }

    #syncActiveFacetPills(selectedFilters) {
      const activeEntries = this.#getActiveFilterEntries(selectedFilters);
      const containers = this.root.querySelectorAll('.active-facets-desktop, .active-facets-mobile');

      for (const container of containers) {
        for (const pill of [...container.querySelectorAll(':scope > facet-remove')]) {
          if (pill.classList.contains('active-facets__button-wrapper')) continue;
          pill.remove();
        }

        this.#ensureClearAllButton(container);

        const clearWrapper = container.querySelector(':scope > facet-remove.active-facets__button-wrapper');
        const verticalFilter = container.querySelector(':scope > .active-facets-vertical-filter');

        for (const entry of activeEntries) {
          const pill = this.#createActiveFacetPill(entry);

          if (verticalFilter) {
            container.appendChild(pill);
          } else if (clearWrapper) {
            container.insertBefore(pill, clearWrapper);
          } else {
            container.appendChild(pill);
          }
        }
      }
    }

    #getActiveFilterEntries(selectedFilters) {
      const entries = [];

      for (const [paramName, values] of Object.entries(selectedFilters)) {
        const filterMeta = this.filtersMeta.find((filter) => filter.paramName === paramName);
        const filterLabel = filterMeta?.label || paramName;

        for (const value of values) {
          const input = this.root.querySelector(
            `input[type="checkbox"][name="${CSS.escape(paramName)}"][value="${CSS.escape(value)}"]`
          );
          const valueLabel =
            input
              ?.closest('label, .facets__label, .facet-checkbox')
              ?.querySelector('.facet-checkbox__text-label')
              ?.textContent?.trim() || value;

          entries.push({
            paramName,
            value,
            filterLabel,
            valueLabel
          });
        }
      }

      return entries;
    }

    #createActiveFacetPill({ paramName, value, filterLabel, valueLabel }) {
      const facetRemove = document.createElement('facet-remove');
      const link = document.createElement('a');
      link.href = '#';
      link.className = 'active-facets__button active-facets__button--light';
      link.setAttribute('data-byte-filter-name', paramName);
      link.setAttribute('data-byte-filter-value', value);
      link.innerHTML = `
        <span class="active-facets__button-inner button button--tertiary">
          ${this.#escapeHtml(filterLabel)}: ${this.#escapeHtml(valueLabel)}
          <span class="svg-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close-small" viewBox="0 0 12 13" aria-hidden="true">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"></path>
            </svg>
          </span>
          <span class="visually-hidden">${this.#escapeHtml(this.#getClearFilterLabel())}</span>
        </span>
      `;
      facetRemove.appendChild(link);
      return facetRemove;
    }

    #ensureClearAllButton(container) {
      let clearAll = container.querySelector('facet-remove.active-facets__button-wrapper a');
      if (!clearAll) {
        const verticalFilter = container.querySelector('.active-facets-vertical-filter');
        const wrapper = document.createElement('facet-remove');
        wrapper.className = 'active-facets__button-wrapper';
        clearAll = document.createElement('a');
        clearAll.href = '#';
        clearAll.className = 'active-facets__button-remove underlined-link';
        clearAll.setAttribute('data-byte-clear-all', '');
        clearAll.innerHTML = `<span>${this.#escapeHtml(this.#getClearAllLabel())}</span>`;
        wrapper.appendChild(clearAll);

        if (verticalFilter) {
          verticalFilter.appendChild(wrapper);
        } else {
          container.appendChild(wrapper);
        }
      }

      clearAll.setAttribute('data-byte-clear-all', '');
      clearAll.setAttribute('href', '#');
    }

    #clearAllFilterInputs() {
      const inputs = this.root.querySelectorAll(
        '#FacetFiltersForm input[type="checkbox"][name^="filter."], #FacetFiltersFormMobile input[type="checkbox"][name^="filter."]'
      );

      for (const input of inputs) {
        input.checked = false;
      }
    }

    #getClearAllLabel() {
      return this.#isTurkish() ? 'Tümünü kaldır' : 'Remove all';
    }

    #getClearFilterLabel() {
      return this.#isTurkish() ? 'Filtreyi kaldır' : 'Remove filter';
    }

    #isTurkish() {
      return (
        document.documentElement.lang?.toLowerCase().startsWith('tr') ||
        window.Shopify?.locale?.toLowerCase?.().startsWith('tr')
      );
    }

    #formatProductCount(count) {
      if (this.#isTurkish()) return `${count} ürün`;
      return count === 1 ? `${count} product` : `${count} products`;
    }

    #clearLoadingState() {
      const collection = this.root.querySelector('#ProductGridContainer .collection');
      if (collection) collection.classList.remove('loading');

      const spinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      for (const spinner of spinners) spinner.classList.add('hidden');
    }

    #cloneFilters(selectedFilters) {
      const clone = {};
      for (const [key, values] of Object.entries(selectedFilters)) {
        clone[key] = [...values];
      }
      return clone;
    }

    #normalize(value) {
      return String(value || '')
        .trim()
        .toLocaleLowerCase('tr-TR');
    }

    #handleize(value) {
      return this.#normalize(value)
        .replace(/[^a-z0-9ğüşıöç]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    #valuesMatch(left, right) {
      const normalizedLeft = this.#normalize(left);
      const normalizedRight = this.#normalize(right);

      if (!normalizedLeft || !normalizedRight) return false;
      if (normalizedLeft === normalizedRight) return true;

      return this.#handleize(normalizedLeft) === this.#handleize(normalizedRight);
    }

    #escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
    }

    #parseJson(selector) {
      const node = this.root.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('Byte limited collection JSON parse failed', error);
        return null;
      }
    }
  }

  function initByteLimitedCollectionFilters() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || root.__byteLimitedFiltersInitialized) return;

    const productsNode = root.querySelector('[data-byte-limited-collection-products]');
    if (!productsNode) return;

    if (typeof FacetFiltersForm === 'undefined') {
      window.setTimeout(initByteLimitedCollectionFilters, 50);
      return;
    }

    root.__byteLimitedFiltersInitialized = true;

    try {
      new ByteLimitedCollectionFilters(root).init();
    } catch (error) {
      root.__byteLimitedFiltersInitialized = false;
      console.error('Byte limited collection filters failed to init', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initByteLimitedCollectionFilters);
  } else {
    // Defer one tick so sibling deferred scripts (facets.js) finish first when needed
    window.setTimeout(initByteLimitedCollectionFilters, 0);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR) || event.target.closest?.(ROOT_SELECTOR);
    if (!root) return;
    root.__byteLimitedFiltersInitialized = false;
    initByteLimitedCollectionFilters();
  });
})();
